import { useEffect, useRef, useState } from "react";
import { COMPANY, api } from "@/lib/config";
import { Phone, Mail, MapPin, Eye } from "lucide-react";
import { Link } from "react-router-dom";

export default function Footer() {
    const [visits, setVisits] = useState(null);
    const visitFired = useRef(false);

    useEffect(() => {
        // Guard against React StrictMode double-mount and same-tab duplicates
        if (visitFired.current) return;
        visitFired.current = true;
        const key = "aba_visit_recorded";
        const alreadyRecorded = sessionStorage.getItem(key);
        const load = async () => {
            try {
                if (!alreadyRecorded) {
                    sessionStorage.setItem(key, "1");
                    const r = await api.post("/visit");
                    setVisits(r.data);
                } else {
                    const r = await api.get("/visits");
                    setVisits(r.data);
                }
            } catch {
                /* fail silently */
            }
        };
        load();
    }, []);

    return (
        <footer className="bg-ink text-cream/80 pt-16 pb-8" data-testid="site-footer">
            <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-4 gap-10">
                <div className="md:col-span-2">
                    <div className="flex items-center gap-3">
                        <span className="w-9 h-9 rounded-sm bg-brand text-cream font-heading text-lg font-semibold flex items-center justify-center">
                            A
                        </span>
                        <span className="font-heading text-lg text-cream tracking-tight">
                            {COMPANY.name}
                        </span>
                    </div>
                    <p className="mt-5 max-w-md text-sm text-cream/65 leading-relaxed">
                        Diagnostic, restauration et formation autour du bâti ancien et
                        des matériaux biosourcés. Pour des maisons qui respirent et durent.
                    </p>
                    {visits && (
                        <div
                            data-testid="visit-counter"
                            className="mt-6 inline-flex items-center gap-2.5 px-3 py-1.5 rounded-sm border border-cream/15 bg-cream/5"
                        >
                            <Eye size={14} className="text-brand" />
                            <span className="text-xs text-cream/75">
                                <strong className="font-heading text-cream tabular-nums">
                                    {visits.total.toLocaleString("fr-FR")}
                                </strong>{" "}
                                visites ·{" "}
                                <strong className="font-heading text-cream tabular-nums">
                                    {visits.unique.toLocaleString("fr-FR")}
                                </strong>{" "}
                                visiteurs uniques
                            </span>
                        </div>
                    )}
                </div>
                <div>
                    <h4 className="label-overline text-cream/50 mb-4">Contact</h4>
                    <ul className="space-y-3 text-sm">
                        <li className="flex gap-2 items-start">
                            <Phone size={14} className="text-brand mt-1" />
                            <a href={`tel:${COMPANY.phoneRaw}`} className="hover:text-brand">
                                {COMPANY.phone}
                            </a>
                        </li>
                        <li className="flex gap-2 items-start">
                            <Mail size={14} className="text-brand mt-1" />
                            <a href={`mailto:${COMPANY.email}`} className="hover:text-brand break-all">
                                {COMPANY.email}
                            </a>
                        </li>
                        <li className="flex gap-2 items-start">
                            <MapPin size={14} className="text-brand mt-1" />
                            <span>{COMPANY.address}</span>
                        </li>
                    </ul>
                </div>
                <div>
                    <h4 className="label-overline text-cream/50 mb-4">Navigation</h4>
                    <ul className="space-y-2.5 text-sm">
                        <li><a href="/#services" className="hover:text-brand">Services</a></li>
                        <li><a href="/#realisations" className="hover:text-brand">Réalisations</a></li>
                        <li><a href="/#apropos" className="hover:text-brand">À propos</a></li>
                        <li><a href="/#formations" className="hover:text-brand">Formations</a></li>
                        <li><a href="/#temoignages" className="hover:text-brand">Témoignages</a></li>
                        <li><a href="/#faq" className="hover:text-brand">FAQ</a></li>
                        <li><a href="/#contact" className="hover:text-brand">Contact</a></li>
                    </ul>
                </div>
            </div>
            <div className="max-w-7xl mx-auto px-6 md:px-10 mt-12 pt-6 border-t border-cream/10 space-y-4">
                <div className="flex flex-wrap items-start gap-x-8 gap-y-2 text-xs text-cream/55" data-testid="footer-legal-info">
                    <span><span className="text-cream/40">SIRET / TVA :</span> FR43 404 536 732</span>
                    <span><span className="text-cream/40">RC pro & décennale :</span> Abeille Assurances — Nonancourt · Contrat n° 77881401</span>
                </div>
                <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between text-xs text-cream/50">
                    <span>© {new Date().getFullYear()} {COMPANY.name}. Tous droits réservés.</span>
                    <div className="flex flex-wrap gap-x-5 gap-y-2">
                        <Link to="/mentions-legales" data-testid="footer-mentions-legales" className="hover:text-brand">Mentions légales</Link>
                        <Link to="/confidentialite" data-testid="footer-confidentialite" className="hover:text-brand">Politique de confidentialité</Link>
                        <span>Zone : {COMPANY.zone}</span>
                    </div>
                </div>
            </div>
        </footer>
    );
}
